import { Component, OnInit, OnDestroy, Input, OnChanges } from '@angular/core';
import { PdfExportService } from 'src/app/core/service/pdf-export.service';
import { DynamicPdfService, StoreAddPdfData, DemographicData } from 'src/app/core/service/dynamic-pdf.service';
import { ToastService } from 'src/app/core/toast/toast.service';
import { DemographicDataService } from 'src/app/core/services/demographic-data.service';
import { KeyIndicatorType } from 'src/app/core/user-preferences/key-indicator-type.enum';
import { Subscription } from 'rxjs';
import { roundToThousand } from 'src/app/core/util/math-utils';

@Component({
  standalone: false,
  selector: 'mtn-store-add-pdf-page',
  templateUrl: './store-add-pdf-page.component.html',
  styleUrls: ['./store-add-pdf-page.component.scss']
})
export class StoreAddPdfPageComponent implements OnInit, OnDestroy, OnChanges {
  @Input() formData: any;
  @Input() summaryData: any;
  @Input() competitiveData: any = [];
  @Input() location: string = '';
  @Input() cbsaName: string = '';
  @Input() regionType: string = '';
  @Input() regionValue: string = '';
  @Input() competitiveRegionType: string = '';
  @Input() competitiveRegionValue: string = '';
  @Input() keyIndicator: string = '';
  @Input() sqftWarningData: any = null;
  @Input() bannerCBSAAverages: any = null;


  exportPdfButtonText = 'Export PDF';
  private subscription: Subscription = new Subscription();
  regionText = '7 minutes Drive Time';
  competitiveRegionText = '7 minutes Drive Time';
  KeyIndicatorType = KeyIndicatorType;
  mapSnapshotUrl: string | null = null;

  // Pagination settings for competitive data
  private readonly ITEMS_PER_PAGE = 21;
  private currentCompetitivePage = 0;

  demographicData = {
    ethnicity: {
      asian: 0,
      hispanic: 0,
      other: 0,
      black: 0,
      white: 0
    },
    population: {
      total: 0,
      growth2yr: 0,
      householdGrowth2yr: 0
    },
    people: {
      averageAge: 0,
      educated: 0,
      householdSize: 0
    },
    income: {
      average: 0,
      median: 0
    }
  };

  bannerAnalysis = {
    banner: '',
    market: '',
    salesSqft: null,
    salesVolume: null,
    storeCount: null
  };


  roundToThousand = roundToThousand;

  constructor(
    private pdfExportService: PdfExportService,
    private dynamicPdfService: DynamicPdfService,
    private toaster: ToastService,
    private demographicDataService: DemographicDataService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.demographicDataService.demographicData$.subscribe((data: any) => {
        if (data.demographics) {
          this.updateDemographicData(data);
          this.updateRegionText(data.regionType, data.regionValue)
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateDemographicData(data: any): void {
    const demographics = data.demographics;
    this.updateRegionText(data.regionType, data.regionValue);

    this.demographicData = {
      ethnicity: {
        asian: this.round(demographics.ethnicityAsian * 100, 1),
        hispanic: this.round(demographics.ethnicityHispanic * 100, 1),
        other: this.round(demographics.ethnicityOther * 100, 1),
        black: this.round(demographics.ethnicityBlack * 100, 1),
        white: this.round(demographics.ethnicityWhite * 100, 1)
      },
      population: {
        total: demographics.populationEstimate,
        growth2yr: this.round(demographics.populationHistoricalGrowth2Year * 100, 2),
        householdGrowth2yr: this.round(demographics.populationProjectedGrowth5Year * 100, 2)
      },
      people: {
        averageAge: demographics.ageAverage,
        educated: this.round(demographics.educationCollegeOrDegree * 100, 1),
        householdSize: demographics.householdsPersonsPer
      },
      income: {
        average: this.round(demographics.householdsAverageIncome,0),
        median: demographics.householdsMedianIncome
      }
    };
  }

  private round(value: number, decimals: number): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  private updateRegionText(regionType: string, regionValue: string): void {
    if (regionType === 'DRIVE_TIME') {
      this.regionText = `${regionValue} Drive Time`;
    } else if (regionType === 'RING') {
      this.regionText = `${regionValue} Ring`;
    } else {
      this.regionText = 'Market Area';
    }
  }

  private updateCompetitiveRegionText(regionType: string, regionValue: string): void {
    if (regionType === 'DRIVE_TIME') {
      this.competitiveRegionText = `${regionValue} Drive Time`;
    } else if (regionType === 'RING') {
      this.competitiveRegionText = `${regionValue} Ring`;
    } else {
      this.competitiveRegionText = 'Market Area';
    }
  }

  ngOnChanges(): void {
    // Update region text when input properties change
    if (this.regionType && this.regionValue) {
      this.updateRegionText(this.regionType, this.regionValue);
    }
    
    // Update competitive region text when input properties change
    if (this.competitiveRegionType && this.competitiveRegionValue) {
      this.updateCompetitiveRegionText(this.competitiveRegionType, this.competitiveRegionValue);
    }

    // Update competitive region text when input properties change
    if (this.competitiveRegionType && this.competitiveRegionValue) {
      this.updateCompetitiveRegionText(this.competitiveRegionType, this.competitiveRegionValue);
    }

    // Log competitive data changes for debugging
    if (this.competitiveData) {
      console.log('Competitive data updated:', this.competitiveData.length, 'items');
    }
  }

  getSqftWarningText(): string {
    if (!this.sqftWarningData) {
      return '';
    }
    const { percentageDifference, avg, averageSqft, grocerName } = this.sqftWarningData;
    console.log("warning data : - ",this.sqftWarningData);
    return `The SQFT value you entered is ${Math.abs(percentageDifference).toFixed(2)}% ${avg.toLowerCase()} the average SQFT for ${grocerName} locations. The average SQFT for ${grocerName} is ${averageSqft} SQFT.`;
  }

  getCompetitiveColumnHeader(): string {
    return this.keyIndicator === KeyIndicatorType.SALES_VOLUME ? 'Sales Volume (In $)' : 'Weekly Sales Per SQFT (In $)';
  }

  getCompetitiveEstLabel(): string {
    return this.keyIndicator === KeyIndicatorType.SALES_VOLUME ? 'Sales Volume (In $)' : 'Weekly Sales Per SQFT (In $)';
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-US');
  }

  formatNumber(value: any): string {
    if (!value) return '--';
    return Number(value).toLocaleString('en-US');
  }

  getWeeklySalesRange(): string {
    if (!this.summaryData?.weeklySalesLowerRange || !this.summaryData?.weeklySalesUpperRange || !this.summaryData?.areaSales) {
      return '--';
    }
    const lower = this.roundToThousand(this.round(this.summaryData.weeklySalesLowerRange * this.summaryData.areaSales,0));
    const upper = this.roundToThousand(this.round(this.summaryData.weeklySalesUpperRange * this.summaryData.areaSales,0));


    return `${this.formatNumber(lower)} - ${this.formatNumber(upper)}`;
  }

  getSalesPerSqftRange(): string {
    if (!this.summaryData?.weeklySalesLowerRange || !this.summaryData?.weeklySalesUpperRange) {
      return '--';
    }
    return `$${this.formatNumber(this.summaryData.weeklySalesLowerRange)} - $${this.formatNumber(this.summaryData.weeklySalesUpperRange)}`;
  }

  getStateAbbreviation(cbsaName: string): string {
    const parts = cbsaName?.split(',');
    return parts?.length > 1 ? parts[1].trim() : cbsaName;
  }

  getCompetitiveDataForCurrentPage(): any[] {
    if (!this.competitiveData || !Array.isArray(this.competitiveData)) {
      return [];
    }
    // Sort competitive data by numeric 'value' descending (highest first) then paginate
    const sorted = [...this.competitiveData].sort((a: any, b: any) => this.getCompetitiveSortValue(b) - this.getCompetitiveSortValue(a));
    const startIndex = this.currentCompetitivePage * this.ITEMS_PER_PAGE;
    const endIndex = startIndex + this.ITEMS_PER_PAGE;
    const result = sorted.slice(startIndex, endIndex);
    console.log(`Competitive data for page ${this.currentCompetitivePage}:`, result.length, 'items', result);
    return result;
  }

  getCompetitiveDataForPage(pageIndex: number): any[] {
    if (!this.competitiveData || !Array.isArray(this.competitiveData)) {
      return [];
    }
    // Sort competitive data by numeric 'value' descending (highest first) then paginate
    const sorted = [...this.competitiveData].sort((a: any, b: any) => this.getCompetitiveSortValue(b) - this.getCompetitiveSortValue(a));
    const startIndex = this.ITEMS_PER_PAGE + (pageIndex * this.ITEMS_PER_PAGE); // First 21 items are on page 2
    const endIndex = startIndex + this.ITEMS_PER_PAGE;
    const result = sorted.slice(startIndex, endIndex);
    console.log(`Additional page ${pageIndex} data:`, result.length, 'items', result);
    return result;
  }

  /**
   * Safely extract a numeric value used for sorting from a competitive data item.
   * Accepts numbers or numeric strings (with $/commas) and falls back to 0.
   */
  private getCompetitiveSortValue(item: any): number {
    if (!item) return 0;
    const v = item.value ?? item?.sales ?? item?.salesPerSqft ?? 0;
    if (typeof v === 'number') return isFinite(v) ? v : 0;
    if (typeof v === 'string') {
      // strip currency symbols and commas
      const cleaned = v.replace(/[^0-9.-]+/g, '');
      const parsed = parseFloat(cleaned);
      return isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  getAdditionalCompetitivePages(): number[] {
    if (!this.competitiveData || !Array.isArray(this.competitiveData) || this.competitiveData.length <= this.ITEMS_PER_PAGE) {
      return [];
    }
    const remainingItems = this.competitiveData.length - this.ITEMS_PER_PAGE;
    const additionalPages = Math.ceil(remainingItems / this.ITEMS_PER_PAGE);
    console.log("🚀 ~ Remaining items:", remainingItems, "Additional pages:", additionalPages);
    return Array.from({ length: additionalPages }, (_, i) => i);
  }

  setMapSnapshot(imageDataUrl: string | null): void {
    if (imageDataUrl) {
      this.mapSnapshotUrl = imageDataUrl;
      console.log('Map snapshot set successfully');

      // Update the map placeholder in the DOM
      const mapPlaceholder = document.querySelector('.map-snapshot-placeholder');
      if (mapPlaceholder) {
        mapPlaceholder.innerHTML = `<img src="${imageDataUrl}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 4px;" />`;
      }
    } else {
      this.mapSnapshotUrl = null;
      console.log('No map image provided');
    }
  }

  async captureMapSnapshot(): Promise<void> {
    // This method is now handled by the parent component
    // Keep for backward compatibility
    this.setMapSnapshot(null);
  }
async generatePdfPages(): Promise<string[]> {
  console.log("🚀 ~ StoreAddPdfPageComponent ~ generatePdfPages ~ generatePdfPages:", this.competitiveData)
  // Base pages
  const pages = ['#pdf-page-1', '#pdf-page-2'];

  // Add additional competitive pages if needed
  if (this.competitiveData && Array.isArray(this.competitiveData) && this.competitiveData.length > this.ITEMS_PER_PAGE) {
    const totalPages = Math.ceil(this.competitiveData.length / this.ITEMS_PER_PAGE);
    const additionalPages = totalPages - 1; // Subtract 1 because first page is already handled in pdf-page-2
    console.log("🚀 ~ StoreAddPdfPageComponent ~ generatePdfPages ~ totalPages:", totalPages);
    console.log("🚀 ~ StoreAddPdfPageComponent ~ generatePdfPages ~ additionalPages:", additionalPages);

    for (let i = 0; i < additionalPages; i++) {
      pages.push(`#pdf-page-competitive-${i + 1}`);
    }
  }

  // Only add demographic page if there are no additional competitive pages
  if (!this.competitiveData || this.competitiveData.length <= this.ITEMS_PER_PAGE) {
    pages.push('#pdf-page-3');
  }
  console.log("🚀 ~ StoreAddPdfPageComponent ~ generatePdfPages ~ pages:", pages)

  console.log('Generated PDF pages:', pages);
  return pages;
}

  getGrocerName(): string {
    if (!this.formData?.grocer) return '--';
    if (typeof this.formData.grocer === 'string') return this.formData.grocer;
    if (typeof this.formData.grocer === 'object' && this.formData.grocer.StoreName) {
      return this.formData.grocer.StoreName;
    }
    return '--';
  }

  getFormattedSqft(): string {
    return this.formatNumber(this.formData?.sqft) || '--';
  }

  getFormattedValue(item: any): string {
    console.log('Formatting value for item:', item);
    if (!item?.value) return '--';
    return Number(item.value).toFixed(2);
  }

  async exportToPdf() {
    // Use the new dynamic PDF generation
    await this.exportToDynamicPdf();
  }

  /**
   * Export using the new dynamic PDF service (native jsPDF elements)
   * This is faster and produces better quality PDFs
   */
  async exportToDynamicPdf() {
    try {
      this.exportPdfButtonText = 'Generating PDF...';

      // Prepare competitive data - sort by value descending and include all required fields
      const sortedCompetitiveData = [...(this.competitiveData || [])].sort((a: any, b: any) =>
        this.getCompetitiveSortValue(b) - this.getCompetitiveSortValue(a)
      );
      const competitiveItems = sortedCompetitiveData.map((item: any) => ({
        name: item.name || item.storeName || '',
        banner: item.banner || '',
        value: item.value || '--',
        count: item.count || item.totalCount || 0
      }));

      // Prepare demographics data
      const demographics: DemographicData = {
        ethnicity: {
          asian: this.demographicData.ethnicity.asian,
          hispanic: this.demographicData.ethnicity.hispanic,
          black: this.demographicData.ethnicity.black,
          white: this.demographicData.ethnicity.white,
          other: this.demographicData.ethnicity.other
        },
        population: {
          total: this.demographicData.population.total,
          growth2yr: this.demographicData.population.growth2yr,
          projected5yr: this.demographicData.population.householdGrowth2yr
        },
        people: {
          averageAge: this.demographicData.people.averageAge,
          collegeEducated: this.demographicData.people.educated,
          householdSize: this.demographicData.people.householdSize
        },
        income: {
          average: this.demographicData.income.average,
          median: this.demographicData.income.median
        },
        regionText: this.regionText
      };

      // Prepare the full data object
      const pdfData: StoreAddPdfData = {
        location: this.location || this.formData?.location || 'Location not provided',
        grocerName: this.getGrocerName(),
        sqft: this.formatNumber(this.formData?.sqft) || '--',
        sqftWarning: this.getSqftWarningText() || undefined,
        weeklySalesRange: this.getWeeklySalesRange(),
        totalSalesProjection: this.summaryData?.totalSalesProjectionStatus || '--',
        totalSalesProjectionLabel: this.bannerCBSAAverages?.cbsa
          ? `Out of ${this.bannerCBSAAverages?.name} in ${this.cbsaName}`
          : 'Relative to Overall Banner Average',
        sqftProjection: this.summaryData?.sqftProjectionStatus || '--',
        sqftProjectionLabel: this.bannerCBSAAverages?.cbsa
          ? `Out of ${this.bannerCBSAAverages?.name} in ${this.cbsaName}`
          : 'Relative to Overall Banner Average',
        closestSameBanner: this.summaryData?.nearestStore?.distance
          ? `${this.summaryData.nearestStore.distance.toFixed(2)} mi`
          : '--',
        closestCompetitor: this.summaryData?.closetCompetitor?.storeName && this.summaryData?.closetCompetitor?.distance
          ? `${this.summaryData.closetCompetitor.storeName} - ${this.summaryData.closetCompetitor.distance.toFixed(2)} mi`
          : '--',
        futureOpenings: `${this.summaryData?.futureStoreCount || 0} / ${this.summaryData?.nearestFutureStoreDistance + ' mi'} (${this.summaryData?.nearestFutureStoreName || '--'})`,
        mapImage: this.mapSnapshotUrl || undefined,
        competitiveData: competitiveItems,
        keyIndicator: this.keyIndicator || KeyIndicatorType.SALES_VOLUME,
        regionText: this.regionText,
        competitiveRegionText: this.competitiveRegionText,
        demographics
      };

      console.log("Summary data : - ", this.summaryData.nearestFutureStoreDistance);

      // Generate PDF using the dynamic service
      await this.dynamicPdfService.generateStoreAddPdf(pdfData, 'store-analysis-report.pdf');

      this.toaster.info('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export failed:', error);
      this.toaster.error('Failed to export PDF');
    } finally {
      this.exportPdfButtonText = 'Export PDF';
    }
  }

  /**
   * Export using the old html2canvas approach (for comparison/fallback)
   */
  async exportToPdfLegacy() {
    try {
      this.exportPdfButtonText = 'Generating PDF...';

      // Generate dynamic page list
      const pages = await this.generatePdfPages();
      console.log("🚀 ~ StoreAddPdfPageComponent ~ exportToPdf ~ pages:", pages)
      const pageGroups = pages.map(page => [page]);
      console.log("🚀 ~ StoreAddPdfPageComponent ~ exportToPdf ~ pageGroups:", pageGroups)

      console.log("Pagegroups : - ",pageGroups);
      await this.pdfExportService.exportToPdfWithPages(pageGroups, 'store-analysis-report.pdf');

      this.toaster.info('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      this.toaster.error('Failed to export PDF');
    } finally {
      this.exportPdfButtonText = 'Export PDF';
    }
  }
}
